import { useCallback, useEffect, useState } from 'react'
import type { SitePageSummary } from './types'
import { api, ApiError } from '../api/client'
import { getRuntime } from './_mode'
import { captureHookFailure, recordHookOutcome } from '../observability/sentry'

export function useSitePages(siteId: string | undefined) {
  const [pages, setPages] = useState<SitePageSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!siteId) {
      setPages([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    const { live } = getRuntime()
    if (!live) {
      setPages([])
      setLoading(false)
      recordHookOutcome('useSitePages', 'demo_fallback', {
        route: '/api/site/pages',
        rows: 0,
      })
      return
    }

    const startedAt = Date.now()
    try {
      const result = await api.site.pages(siteId)
      setPages(result.pages || [])
      recordHookOutcome('useSitePages', 'live_success', {
        route: '/api/site/pages',
        rows: result.pages?.length || 0,
        durationMs: Date.now() - startedAt,
      })
    } catch (err) {
      const apiErr = err as ApiError
      setPages([])
      setError(apiErr?.message || 'site_pages_failed')
      captureHookFailure('useSitePages', 'live_error', err, {
        route: '/api/site/pages',
        code: apiErr?.code,
        status: apiErr?.status,
      })
    } finally {
      setLoading(false)
    }
  }, [siteId])

  useEffect(() => {
    void load()
  }, [load])

  return { pages, loading, error, refresh: load }
}
